'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { Fragment, type MouseEvent, useMemo, useRef } from 'react';
import AppLink from '@/components/elementary/AppLink';
import { isDefaultLocale, sortLocaleLinksByAppLocales } from '@/components/elementary/LocaleSwitcher/utils';
import { Check } from '@/components/icons/Check';
import { Dropdown } from '@/components/icons/Dropdown';
import { Globe } from '@/components/icons/Globe';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { LOCALE_TRANSLATION } from '@/constants/i18n.constants';
import { cn } from '@/lib/styles';
import type { AppLocale } from '@/types/general';
import type { LocaleLink } from '@/types/i18n.types';
type Props = {
  currentLocale: AppLocale;
  localeLinks: LocaleLink[];
  onLocaleChange?: (locale: AppLocale) => void;
  showDropdownIcon?: boolean;
};
const LocaleSwitcher = t0 => {
  const $ = _c(29);
  const {
    currentLocale,
    localeLinks,
    onLocaleChange,
    showDropdownIcon: t1
  } = t0;
  const showDropdownIcon = t1 === undefined ? true : t1;
  const t = useTranslations("LocaleSwitcher");
  let t2;
  if ($[0] !== localeLinks) {
    t2 = sortLocaleLinksByAppLocales(localeLinks);
    $[0] = localeLinks;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sortedLinks = t2;
  const isOpenedWithPointerRef = useRef(false);
  let t3;
  if ($[2] !== onLocaleChange) {
    t3 = locale => event => {
      if (!onLocaleChange) {
        return;
      }
      event.preventDefault();
      onLocaleChange(locale);
    };
    $[2] = onLocaleChange;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleLocaleLinkClick = t3;
  let t4;
  if ($[4] !== t) {
    t4 = t("selectLanguage");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      isOpenedWithPointerRef.current = true;
    };
    t6 = () => {
      isOpenedWithPointerRef.current = false;
    };
    t7 = <Globe className="size-6" />;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[9] !== showDropdownIcon) {
    t8 = showDropdownIcon && <Dropdown className="size-6" />;
    $[9] = showDropdownIcon;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t4 || $[12] !== t8) {
    t9 = <DropdownMenuTrigger asChild={true}><Button type="button" variant="secondary" size="none" aria-label={t4} className="h-10.5 w-fit justify-between px-2 py-2.5 lok-text-body-bold-medium uppercase text-nowrap" onPointerDown={t5} onKeyDown={t6}>{t7}{t8}</Button></DropdownMenuTrigger>;
    $[11] = t4;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      isOpenedWithPointerRef.current = false;
    };
    t11 = event_0 => {
      if (isOpenedWithPointerRef.current) {
        event_0.preventDefault();
      }
    };
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  let t12;
  if ($[16] !== currentLocale || $[17] !== handleLocaleLinkClick || $[18] !== sortedLinks) {
    let t13;
    if ($[20] !== currentLocale || $[21] !== handleLocaleLinkClick || $[22] !== sortedLinks.length) {
      t13 = (link, index) => {
        const isActive = link.locale === currentLocale;
        const useNativeAnchor = isDefaultLocale(link.locale);
        return <Fragment key={link.locale}><DropdownMenuItem asChild={true} className="p-0"><AppLink href={link.pathname} hrefMode="localized" locale={link.locale} forceAnchor={useNativeAnchor} variant="none" size="none" lang={link.locale} aria-current={isActive ? "page" : undefined} className={cn("relative w-full rounded-sm px-2 py-1.5 pr-8", "hover:bg-accent focus:bg-accent focus:text-accent-foreground cursor-pointer", isActive ? "lok-text-body-bold-medium" : "lok-text-body-regular-medium")} onClick={handleLocaleLinkClick(link.locale)}><span>{LOCALE_TRANSLATION[link.locale as keyof typeof LOCALE_TRANSLATION]}</span>{isActive && <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center"><Check className="size-3" /></span>}</AppLink></DropdownMenuItem>{index < sortedLinks.length - 1 && <DropdownMenuSeparator />}</Fragment>;
      };
      $[20] = currentLocale;
      $[21] = handleLocaleLinkClick;
      $[22] = sortedLinks.length;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    t12 = sortedLinks.map(t13);
    $[16] = currentLocale;
    $[17] = handleLocaleLinkClick;
    $[18] = sortedLinks;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[24] !== t12) {
    t13 = <DropdownMenuContent className="hidden xl:block rounded-sm border-none shadow-box" side="bottom" align="end" sideOffset={18} onKeyDown={t10} onCloseAutoFocus={t11}>{t12}</DropdownMenuContent>;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t9) {
    t14 = <DropdownMenu>{t9}{t13}</DropdownMenu>;
    $[26] = t13;
    $[27] = t9;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
};
export default LocaleSwitcher;